import { type LeagueScoringMode, type ScoringEvidence } from './league-rules.js';
/**
 * HISTORICAL LEAGUE SCORING. Structured Social/Tournament evidence dispatches to
 * league-rules.ts; these older bonuses remain for leagues without the new mode.
 *
 * Taken from the real score card (`docs/design/Scoring Instructions League.pdf`, supplied
 * 2026-08-25), which settled the question this package carried a warning about for two months.
 * These are Evergreen's league rules layered on top of the NMJL card — not NMJL's own scoring, and
 * not a guess.
 *
 * ONE GAME, ONE WINNER. She scores the value printed against that hand on the current NMJL card,
 * plus any bonuses below. Everybody else at the table scores nothing for that game.
 *
 * THE EXCEPTION IS A WALL GAME. Nobody declares mahjong, the wall runs out, and all four players
 * record 10. It is the only way a player scores without winning, which is exactly why it is worth
 * recording as its own thing rather than as four tens somebody typed.
 *
 * BONUSES ARE FOR DOING SOMETHING HARDER THAN THE HAND REQUIRED. That single idea explains both
 * exceptions, and getting it wrong was my first reading:
 *
 *   Self-picked          +10   Always. Drawing your own winning tile is harder than claiming one.
 *   Jokerless            +10   EXCEPT Singles and Pairs, which cannot use jokers at all — there is
 *                              no restraint to reward.
 *   No exposures         +10   EXCEPT a hand the card already marks Concealed. Keeping an
 *                              EXPOSED hand closed when you were entitled to expose is the
 *                              achievement; being concealed because the card said so is not.
 *
 * A SESSION COUNTS UP TO THE BEST FOUR GAMES. A table may play up to eight, and only the
 * top four earnings count toward her total (with all Tournament deductions). That is the reason this platform records each game
 * separately instead of a single number per session — "best four" cannot be recovered from a sum.
 *
 * Pure, like everything else in this package: numbers in, numbers out, no I/O and no clock.
 */

/** Historical four-game card/fixture size, not a scoring or rating eligibility minimum. */
export const GAMES_REQUIRED = 4;

/** The most a table may play in one session (owner, 2026-08-25). */
export const GAMES_MAX = 8;

/** How many of them count toward her total. */
export const GAMES_COUNTED = 4;

/** What everybody records when the wall runs out. */
export const WALL_GAME_POINTS = 10;

export const BONUS_POINTS = 10;

/**
 * What a hand can earn on top of its card value. Named as questions a director can answer from the
 * paper card without interpreting anything.
 */
export interface HandBonuses {
  /** She drew the winning tile herself rather than claiming a discard. */
  selfPicked?: boolean;
  /** She won without a joker. Worth nothing on Singles and Pairs, which forbid them anyway. */
  jokerless?: boolean;
  /** She made no exposures. Worth nothing if the card already required the hand to be concealed. */
  noExposures?: boolean;
}

export interface HandFacts extends HandBonuses {
  /** The value printed against that hand on the current NMJL card. */
  cardValue: number;
  /** The hand is in the card's Singles and Pairs section, so jokers were never allowed. */
  singlesAndPairs?: boolean;
  /** The card marks this hand Concealed, so staying closed was compulsory. */
  concealedHand?: boolean;
}

/**
 * What one winning hand is worth.
 *
 * The director does not normally call this — East has already done the arithmetic on the paper
 * card and she types the total. It exists so the app can CHECK her arithmetic when she gives it the
 * parts, and so the rule lives in code rather than only in a PDF.
 */
export function scoreHand(hand: HandFacts): number {
  let points = hand.cardValue;

  if (hand.selfPicked) points += BONUS_POINTS;

  /** No credit for a restriction the hand imposed on her anyway. See the module comment. */
  if (hand.jokerless && !hand.singlesAndPairs) points += BONUS_POINTS;
  if (hand.noExposures && !hand.concealedHand) points += BONUS_POINTS;

  return points;
}

/** One player's result in one game. `points` is what East wrote down, bonuses included. */
export interface GameResult {
  evidence?: ScoringEvidence | null;
  gameNumber: number;
  points: number;
  /** True for the player who declared mahjong. False for everybody in a wall game. */
  won: boolean;
}

export interface SessionTotal {
  /** The sum of her best `GAMES_COUNTED` games. */
  points: number;
  /** How many games she won. Every game she played counts here, not only the four that scored. */
  handsWon: number;
  /** How many games the table actually played. */
  gamesPlayed: number;
  /** The game numbers that made up the total, so a director can see which four counted. */
  countedGames: number[];
  /** True when at least one completed game has been recorded. */
  complete: boolean;
}

/**
 * Her total for the session: the best four games, and which four they were.
 *
 * Returning the game numbers rather than just the sum is deliberate. When a table plays six and
 * two are dropped, the first question anybody asks is "which two?" — and a total that cannot answer
 * it is a number she has to take on trust.
 */
export function sessionTotal(games: GameResult[], mode?: LeagueScoringMode): SessionTotal {
  const effectiveMode = mode ?? games.find(g=>g.evidence)?.evidence?.mode;
  if (effectiveMode) {
    if (games.some(g=>g.evidence && g.evidence.mode !== effectiveMode)) throw new Error('Mixed scoring modes.');
    const values = games.map(g=>({
      gameNumber:g.gameNumber,
      hasEarnings: Boolean(g.evidence) || g.points >= 0,
      earnings:g.evidence ? g.evidence.result.earnings : Math.max(0,g.points),
      deductions:g.evidence ? g.evidence.result.deductions.reduce((n,d)=>n+d.amount,0) : -Math.min(0,g.points),
      complete:g.evidence?.result.outcome !== 'incomplete',
      won:g.evidence ? g.evidence.result.won : g.won,
    }));
    const best = values.filter(g=>g.complete && g.hasEarnings).sort((a,b)=>b.earnings-a.earnings || a.gameNumber-b.gameNumber).slice(0,GAMES_COUNTED);
    return {points:best.reduce((n,g)=>n+g.earnings,0)-(effectiveMode==='tournament'?values.filter(g=>g.complete).reduce((n,g)=>n+g.deductions,0):0),handsWon:values.filter(g=>g.complete && g.won).length,gamesPlayed:values.filter(g=>g.complete).length,countedGames:best.map(g=>g.gameNumber).sort((a,b)=>a-b),complete:values.some(g=>g.complete)};
  }
  const played = [...games].sort((a, b) => a.gameNumber - b.gameNumber);

  const best = [...played]
    .sort((a, b) => b.points - a.points || a.gameNumber - b.gameNumber)
    .slice(0, GAMES_COUNTED);

  return {
    points: best.reduce((total, game) => total + game.points, 0),
    /**
     * Counted across EVERY game, not only the four that scored. A fifth-game win that was dropped
     * from her total still happened, and hands won is a tiebreaker — quietly discarding it would
     * make the tiebreak depend on which games the arithmetic kept.
     */
    handsWon: played.filter((game) => game.won).length,
    gamesPlayed: played.length,
    countedGames: best.map((game) => game.gameNumber).sort((a, b) => a - b),
    complete: played.length > 0,
  };
}

/**
 * The four scores a wall game produces, ready to be recorded like any other game.
 *
 * Written as a helper rather than left to the caller because "everybody gets ten" is the one rule
 * on the card that touches players who did nothing, and four tens typed by hand are four chances
 * to type three.
 */
export function wallGame(playerIds: string[], gameNumber: number): (GameResult & { playerId: string })[] {
  return playerIds.map((playerId) => ({
    playerId,
    gameNumber,
    points: WALL_GAME_POINTS,
    won: false,
  }));
}

/** Signed display value, retaining the stored game evidence underneath. */
export function gameEntryPoints(game: GameResult): number {
  return game.evidence ? game.evidence.result.earnings - (game.evidence.mode === 'tournament' ? game.evidence.result.deductions.reduce((n,d)=>n+d.amount,0) : 0) : game.points;
}

/** Every table member must have the same nonempty set of completed games. */
export function tableScoresComplete(players: GameResult[][], mode?: LeagueScoringMode): boolean {
  const completed = players.map(games => games
    .filter(game => sessionTotal([game], mode).complete)
    .map(game => game.gameNumber).sort((a, b) => a - b).join(','));
  return completed.length > 0 && completed[0] !== '' && completed.every(games => games === completed[0]);
}
